import React, { useState, useRef, useEffect } from "react";
import "../../css/designSystem/Navbar.css";
import logo from "../../assets/icons/navbar_logo.svg";
import TertiaryButton from "./buttons/TertiaryButton";
import { useLocation, useNavigate } from "react-router-dom";
import PrimaryButton from "./buttons/PrimaryButton";
import {ReactComponent as FacebookIcon} from "../../assets/icons/facebook.svg";
import {ReactComponent as InstaIcon} from "../../assets/icons/instagram.svg";
import {ReactComponent as LinkedInIcon} from "../../assets/icons/linkedin.svg";

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMortgageDropdown, setShowMortgageDropdown] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [showCalculatorsDropdown, setShowCalculatorsDropdown] = useState(false);
  const mortgageRef = useRef(null);
  const servicesRef = useRef(null);
  const toolsRef = useRef(null);
  const calculatorsRef = useRef(null);

  // Close dropdown if clicked outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (mortgageRef.current && !mortgageRef.current.contains(event.target)) {
        setShowMortgageDropdown(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setShowServicesDropdown(false);
      }
      if (toolsRef.current && !toolsRef.current.contains(event.target)) {
        setShowToolsDropdown(false);
      }
      if (
        calculatorsRef.current &&
        !calculatorsRef.current.contains(event.target)
      ) {
        setShowCalculatorsDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!location.hash) return;

    const sectionId = location.hash.slice(1);
    window.setTimeout(() => {
      document
        .getElementById(sectionId)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, [location.pathname, location.hash]);

  const mortgageList = [
    { name: "Find a Mortgage", path: "/find-a-mortgage" },
    { name: "First Time Buyer", path: "/find-a-mortgage", sectionId: "first-time-buyers" },
    { name: "Buy-to-Let Mortgages", path: "/find-a-mortgage", sectionId: "buy-to-let-mortgages" },
    { name: "Remortgage", path: "/find-a-mortgage", sectionId: "remortgage" },
    { name: "Home Movers", path: "/find-a-mortgage", sectionId: "home-movers" },
  ];

  const protectionList = [
    { name: "Insurance & Protection", path: "/insurance-protection" },
    { name: "Life Insurance", path: "/insurance-protection", sectionId: "life-insurance" },
    { name: "Critical Illness Cover", path: "/insurance-protection", sectionId: "critical-illness-cover" },
    { name: "Income Protection", path: "/insurance-protection", sectionId: "why-consider-protection" },
    { name: "Home Buyers Protection", path: "/insurance-protection", sectionId: "home-buyers-protection" },
  ];

  const toolsList = [
    { name: "Useful Tools", path: "/useful-tools" },
    { name: "Repayment Calculator", path: "/useful-tools", sectionId: "repayment-calculator" },
    { name: "Stamp Duty Calculator", path: "/useful-tools", sectionId: "stamp-duty-calculator" },
    { name: "First-Time Buyers Guide", path: "/useful-tools", sectionId: "first-time-buyers-guide" },
    { name: "Buy-to-Let Guide", path: "/useful-tools", sectionId: "buy-to-let-guide" },
    { name: "Tax Guide", path: "/useful-tools", sectionId: "tax-guide" },
  ];

  const aboutUsList = [
    { name: "Who We Are", path: "/about-us/who-we-are" },
    { name: "Honest Pricing", path: "/about-us/honest-pricing" },
    { name: "Charity Partnerships", path: "/about-us/charity-partnerships" },
  ];

  // const calculatorsList = [
  //   { name: "Mortgage Borrow", path: "/calculators/mortgage-borrow" },
  //   { name: "Mortgage Repayment", path: "/calculators/mortgage-repayment" },
  //   { name: "Overpayment", path: "/calculators/overpayment" },
  //   { name: "Stamp Duty", path: "/calculators/stamp-duty" },
  //   {
  //     name: "How Much Will It Cost",
  //     path: "/calculators/how-much-will-it-cost",
  //   },
  //   {
  //     name: "How Much Can I Borrow",
  //     path: "/calculators/how-much-can-i-borrow",
  //   },
  //   { name: "Equity Release", path: "/calculators/equity-release" },
  //   { name: "Relevant Life Tax", path: "/calculators/relevant-life-tax" },
  //   { name: "Pensions Shortfall", path: "/calculators/pensions-shortfall" },
  //   { name: "Inheritance Tax", path: "/calculators/inheritance-tax" },
  //   { name: "Investment", path: "/calculators/investment" },
  // ];

  const handleNavigate = (path) => {
    setShowMortgageDropdown(false);
    setShowServicesDropdown(false);
    setShowToolsDropdown(false);
    setShowCalculatorsDropdown(false);
    setIsMobileMenuOpen(false);
    navigate(path);
  };

  const handleSectionNavigate = ({ path, sectionId }) => {
    const nextPath = sectionId ? `${path}#${sectionId}` : path;
    handleNavigate(nextPath);

    if (location.pathname === path && sectionId) {
      window.setTimeout(() => {
        document
          .getElementById(sectionId)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    }
  };

  const handleContactClick = () => {
    setShowMortgageDropdown(false);
    setShowServicesDropdown(false);
    setShowToolsDropdown(false);
    setShowCalculatorsDropdown(false);
    setIsMobileMenuOpen(false);
    document
      .getElementById("contact-us")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const navTextClass = (path, exact = true) => {
    const isActive = exact
      ? location.pathname === path
      : location.pathname.startsWith(path);

    return `nav-text ${isActive ? "is-active" : ""}`;
  };

  const dropdownItemClass = (path) => {
    return `navbar-dropdown-item content ${
      location.pathname === path ? "is-active" : ""
    }`;
  };

  const sectionDropdownItemClass = ({ path, sectionId }) => {
    const isActive =
      location.pathname === path &&
      (!sectionId || location.hash === `#${sectionId}`);

    return `navbar-dropdown-item content ${isActive ? "is-active" : ""}`;
  };

  return (
    <nav className="navbar">
      <div className="navbar-main-row">
        <div className="navbar-left">
          <img
            src={logo}
            alt="AK Mortgage Solutions logo"
            className="navbar-logo"
            onClick={() => {
              handleNavigate("/");
            }}
          />
        </div>

        <button
          type="button"
          className={`navbar-toggle ${isMobileMenuOpen ? "is-open" : ""}`}
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
          onClick={() => {
            setIsMobileMenuOpen((prev) => !prev);
            setShowMortgageDropdown(false);
            setShowServicesDropdown(false);
            setShowToolsDropdown(false);
            setShowCalculatorsDropdown(false);
          }}
        >
          <span className="navbar-toggle-line line-1" />
          <span className="navbar-toggle-line line-2" />
          <span className="navbar-toggle-line line-3" />
        </button>
      </div>

      <div className={`navbar-content ${isMobileMenuOpen ? "is-open" : ""}`}>
        <ul className="navbar-links">
          <li ref={mortgageRef} style={{ position: "relative" }}>
            <span
              className={`${navTextClass("/find-a-mortgage")} has-dropdown`}
              aria-haspopup="true"
              aria-expanded={showMortgageDropdown}
              onClick={() => {
                setShowMortgageDropdown((prev) => !prev);
                setShowServicesDropdown(false);
                setShowToolsDropdown(false);
                setShowCalculatorsDropdown(false);
              }}
            >
              Find a Mortgage
            </span>
            {showMortgageDropdown && (
              <ul className="navbar-dropdown">
                {mortgageList.map((item) => (
                  <li
                    key={`${item.path}-${item.sectionId || "top"}`}
                    className={sectionDropdownItemClass(item)}
                    onClick={() => {
                      handleSectionNavigate(item);
                    }}
                  >
                    {item.name}
                  </li>
                ))}
              </ul>
            )}
          </li>
          <li ref={servicesRef} style={{ position: "relative" }}>
            <span
              className={`${navTextClass("/insurance-protection")} has-dropdown`}
              aria-haspopup="true"
              aria-expanded={showServicesDropdown}
              onClick={() => {
                setShowMortgageDropdown(false);
                setShowServicesDropdown((prev) => !prev);
                setShowToolsDropdown(false);
                setShowCalculatorsDropdown(false);
              }}
            >
              Insurance & protection
            </span>
            {showServicesDropdown && (
              <ul className="navbar-dropdown">
                {protectionList.map((item) => (
                  <li
                    key={`${item.path}-${item.sectionId || "top"}`}
                    className={sectionDropdownItemClass(item)}
                    onClick={() => {
                      handleSectionNavigate(item);
                    }}
                  >
                    {item.name}
                  </li>
                ))}
              </ul>
            )}
          </li>
          <li ref={toolsRef} style={{ position: "relative" }}>
            <span
              className={`${navTextClass("/useful-tools")} has-dropdown`}
              aria-haspopup="true"
              aria-expanded={showToolsDropdown}
              onClick={() => {
                setShowMortgageDropdown(false);
                setShowServicesDropdown(false);
                setShowToolsDropdown((prev) => !prev);
                setShowCalculatorsDropdown(false);
              }}
              style={{ userSelect: "none" }}
            >
              Useful Tools
            </span>
            {showToolsDropdown && (
              <ul className="navbar-dropdown">
                {toolsList.map((item) => (
                  <li
                    key={`${item.path}-${item.sectionId || "top"}`}
                    className={sectionDropdownItemClass(item)}
                    onClick={() => {
                      handleSectionNavigate(item);
                    }}
                  >
                    {item.name}
                  </li>
                ))}
              </ul>
            )}
          </li>
          <li ref={calculatorsRef} style={{ position: "relative" }}>
            <span
              className={`${navTextClass("/about-us", false)} has-dropdown`}
              aria-haspopup="true"
              aria-expanded={showCalculatorsDropdown}
              onClick={() => {
                setShowMortgageDropdown(false);
                setShowServicesDropdown(false);
                setShowToolsDropdown(false);
                setShowCalculatorsDropdown((prev) => !prev);
              }}
            >
              About Us
            </span>
            {showCalculatorsDropdown && (
              <ul className="navbar-dropdown">
                {aboutUsList.map((item) => (
                  <li
                    key={item.name}
                    className={dropdownItemClass(item.path)}
                    onClick={() => {
                      handleNavigate(item.path);
                    }}
                  >
                    {item.name}
                  </li>
                ))}
              </ul>
            )}
          </li>
          {/* <li>
            <span
              className={navTextClass("/blogs")}
              onClick={() => {
                handleNavigate("/blogs");
              }}
            >
              Blogs
            </span>
          </li> */}
        </ul>

        <div className="navbar-actions gap-m row-center">
          {/* <TertiaryButton
            onClick={() => {
              handleNavigate("/contact-us");
            }}
            text="Speak to Us"
          /> */}
          <div className="row-wrap gap-s">
            <FacebookIcon
              className="mobile-block"
              onClick={() => {
                window.open(
                  "https://www.facebook.com/share/1EFhLwhaRU/",
                  "_blank",
                );
              }}
            />
            <a
              href="https://www.instagram.com/akmortgagesolutions/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <InstaIcon className="mobile-block" />
            </a>
            <LinkedInIcon
              className="mobile-block"
              onClick={() => {
                window.open(
                  "https://www.linkedin.com/company/ak-mortgages/about/?viewAsMember=true",
                  "_blank",
                );
              }}
            />
          </div>
          <PrimaryButton onClick={handleContactClick} text="Contact US" />
        </div>

        {/* <div className="navbar-mobile-menu">
          <button
            type="button"
            className="navbar-mobile-link"
            onClick={() => {
              handleNavigate("/services/mortgages");
            }}
          >
            Find A Mortgage
          </button>
          <button
            type="button"
            className="navbar-mobile-link"
            onClick={() => {
              handleNavigate("/services/protection");
            }}
          >
            Insurance & Protection
          </button>
          <button
            type="button"
            className="navbar-mobile-link"
            onClick={() => {
              handleNavigate("/about-us");
            }}
          >
            Honest Pricing
          </button>
          <button
            type="button"
            className="navbar-mobile-link"
            onClick={() => {
              handleNavigate("/calculators/mortgage-borrow");
            }}
          >
            Useful Tools
          </button>
          <button
            type="button"
            className="navbar-mobile-link"
            onClick={() => {
              handleNavigate("/about-us");
            }}
          >
            About Us
          </button>
          <button
            type="button"
            className="navbar-mobile-link"
            onClick={() => {
              handleNavigate("/blogs");
            }}
          >
            Blogs
          </button>

          <button
            type="button"
            className="navbar-mobile-cta"
            onClick={() => {
              handleNavigate("/contact-us");
            }}
          >
            Contact Us
          </button>
        </div> */}
      </div>
    </nav>
  );
};

export default Navbar;
